import { Icon, Label, Tooltip, token } from "@may-i/mash-design-system";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import type { Camera } from "~/entities/camera/types";
import { isCameraAnalyzed } from "~/entities/camera/utils";
import { IcEraser } from "~/shared/assets/icons/IcEraser";
import { TEMP_IMAGE_URL } from "~/shared/ui/EmptyImagePlaceholder";

type CardState = "selected" | "default" | "temp";

type LocalMappingType = "add" | "edit" | null;

interface MappingCameraCardProps {
  camera: Camera;
  state: CardState;
  localMappingType?: LocalMappingType;
  displayNumber?: string | null;
  linkCount?: number;
  memoCount?: number;
  overrideImageUrl?: string;
  onClick: (camera: Camera) => void;
  onMemoClick?: (camera: Camera) => void;
  onRemoveFromFloorPlan?: (camera: Camera) => void;
}

export function MappingCameraCard({
  camera,
  state,
  localMappingType = null,
  displayNumber = null,
  linkCount = 0,
  memoCount = 0,
  overrideImageUrl,
  onClick,
  onMemoClick,
  onRemoveFromFloorPlan,
}: MappingCameraCardProps) {
  const { t } = useTranslation("camera-mapping");
  const isAnalyzed = isCameraAnalyzed(camera.analysisNumber);
  const imageUrl = overrideImageUrl ?? camera.previewImageUrl ?? camera.originalImageUrl;

  const badgeLabel =
    localMappingType === "add"
      ? t("card.added")
      : localMappingType === "edit"
        ? t("card.adjusted")
        : null;

  const removeDisabled = isAnalyzed;
  const removeTooltip = removeDisabled
    ? t("canvas.removeFromFloorPlanDisabled")
    : t("canvas.removeFromFloorPlan");

  return (
    <Card $state={state} data-camera-id={camera.id} onClick={() => onClick(camera)}>
      <ImageArea>
        <CardImage src={imageUrl || TEMP_IMAGE_URL} alt={camera.defaultCamId} />
        {badgeLabel && <MappedBadge>{badgeLabel}</MappedBadge>}
        {onRemoveFromFloorPlan && (
          <Tooltip
            trigger={
              <RemoveButton
                type="button"
                disabled={removeDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!removeDisabled) onRemoveFromFloorPlan(camera);
                }}
                aria-label={removeTooltip}
              >
                <IcEraser width={10} height={10} />
              </RemoveButton>
            }
            side="top"
            align="end"
            sideOffset={4}
          >
            {removeTooltip}
          </Tooltip>
        )}
      </ImageArea>
      <InfoRow>
        <InfoLeft>
          <Label
            variant="soft"
            color={isAnalyzed ? "success" : "default"}
            size="xSmall"
          >
            {isAnalyzed ? t("card.analysis") : t("card.noAnalysis")}
          </Label>
          <CardName>{displayNumber ?? camera.defaultCamId}</CardName>
        </InfoLeft>
        <InfoRight>
          {linkCount > 0 && (
            <MetaCount>
              <Icon icon="ic_link_chain" width={14} height={14} />
              <span>{linkCount}</span>
            </MetaCount>
          )}
          <MemoCountButton
            onClick={(e) => {
              e.stopPropagation();
              onMemoClick?.(camera);
            }}
          >
            <Icon icon="ic_chart_description" width={12} height={12} />
            <span>{memoCount}</span>
          </MemoCountButton>
        </InfoRight>
      </InfoRow>
    </Card>
  );
}

// --- Styles ---

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.88);
  color: ${token("colors.text.primary")};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${token("colors.gray.white")};
  }

  &:disabled {
    color: ${token("colors.text.disabled")};
    cursor: not-allowed;
  }
`;

const Card = styled.div<{ $state: CardState }>`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(143, 149, 178, 0.32);
  transition: border-color 0.15s ease;

  /* Tooltip 이 RemoveButton 을 .tooltip-trigger-wrapper div 로 감싸기 때문에
     position 과 hover-opacity 를 wrapper 에 적용해야 tooltip anchor 가 일치한다 */
  & .tooltip-trigger-wrapper {
    position: absolute;
    top: 4px;
    right: 4px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover .tooltip-trigger-wrapper {
    opacity: 1;
  }

  ${({ $state }) => {
    switch ($state) {
      case "selected":
        return css`
          border-color: #484bd2;
          box-shadow: 0 0 0 1px #484bd2;
        `;
      case "temp":
        return css`
          border-style: dashed;
        `;
      default:
        return css``;
    }
  }}

  &:hover {
    border-color: #484bd2;
  }
`;

const ImageArea = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${token("colors.gray.GR50")};
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  /* 카드는 격자 정렬 때문에 16:9 박스를 유지하되, 스냅샷을 잘라내지는 않는다
     (정사각형/4:3 카메라는 좌우 여백) */
  object-fit: contain;
`;

const MappedBadge = styled.span`
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 4px;
  border-radius: 4px;
  background: ${token("colors.action.disabled")};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  color: #fff;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
`;

const InfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const CardName = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InfoRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const MetaCount = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  color: ${token("colors.text.secondary")};

  > span {
    font-family: Pretendard Variable, Pretendard, sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.5;
  }
`;

const MemoCountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${token("colors.text.secondary")};

  > span {
    font-family: Pretendard Variable, Pretendard, sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.5;
  }

  &:hover {
    color: ${token("colors.text.primary")};
  }
`;
